import React from 'react';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../../i18n/TModules';
import { prizeComplete } from '../../adapters/common';
import { limitErrors } from '../../adapters/limits';
import StepPanel from '../../kit/StepPanel';
import { Divider, Form } from '../../kit/layout';
import { EnrolmentFields, LimitLine, ScheduleFields, WindowLine } from '../../kit/limitFields';
import { IWizardStepProps, newPrize, WizardLimits } from '../../wizardModel';
import PrizeList from '../PrizeList';

const TW = `${TOrbit}.wizard`;
const TL = `${TW}.limits`;

/** 6778:22620, Step 4/5: what completing every task earns. */
export const QuestPrizeStep: React.FC<IWizardStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  return (
    <StepPanel
      step="prizes"
      title={t(`${TW}.setup.quest`)}
      question={t(`${TW}.prize.questQuestion`)}
      errors={state.prizes.every(prizeComplete) ? [] : [t(`${TW}.prize.incomplete`)]}
      onNext={next}
      onBack={back}
    >
      <PrizeList
        addLabel={t(`${TW}.prize.addPrize`)}
        name="orbit-wizard-quest-prize"
        prizes={state.prizes}
        eyebrow={() => t(`${TW}.prize.prize`)}
        blank={() => ({ ...newPrize(), slot: state.prizes.length + 1 })}
        onChange={prizes => update(current => ({ ...current, prizes }))}
      />
    </StepPanel>
  );
};

/** 6778:22854, Step 5/5: two quest limits, enrolment and its window, then the dates. */
export const QuestLimitsStep: React.FC<IWizardStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  const { limits } = state;
  const change = (patch: Partial<WizardLimits>) =>
    update(current => ({ ...current, limits: { ...current.limits, ...patch } }));

  return (
    <StepPanel
      step="limits"
      title={t(`${TW}.setup.quest`)}
      question={t(`${TL}.question`)}
      errors={limitErrors(state, t)}
      onNext={next}
      onBack={back}
    >
      <Form>
        <LimitLine
          on={limits.totalOn}
          onToggle={totalOn => change({ totalOn })}
          leadIn={t(`${TL}.questParticipants`)}
          value={limits.total}
          onChange={total => change({ total })}
          unit={t(`${TL}.questParticipantsUnit`)}
        />
        <LimitLine
          on={limits.perUserOn}
          onToggle={perUserOn => change({ perUserOn })}
          leadIn={t(`${TL}.questCompletions`)}
          value={limits.perUser}
          onChange={perUser => change({ perUser })}
          unit={t(`${TL}.questCompletionsUnit`)}
        />
        <Divider />
        <EnrolmentFields limits={limits} onChange={change} withTime>
          {!limits.autoEnrol ? (
            <WindowLine limits={limits} onChange={change} leadIn={t(`${TL}.window.quest`)} />
          ) : null}
        </EnrolmentFields>
        <ScheduleFields limits={limits} onChange={change} />
      </Form>
    </StepPanel>
  );
};
